import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShoppingBag, CreditCard, Check, ArrowRight } from "lucide-react";
import { fetchProducts } from "../lib/api";
import ContactSection from "../components/ContactSection";

export default function Butik() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProducts() {
      const data = await fetchProducts();
      setProducts(data);
      setLoading(false);
    }
    loadProducts();
  }, []);

  return (
    <>
      <Helmet>
        <title>Online Butik – Book Sessioner & Medlemskab | John Calmar</title>
        <meta
          name="description"
          content="Køb medlemskab af John Calmars Univers, online klarsynsaftener, clairvoyance-sessioner, rygestop-forløb og hypnose/hypnoterapi-sessioner."
        />
        <link rel="canonical" href="https://johncalmar.dk/butik" />
        <meta property="og:title" content="Online Butik – Book Sessioner & Medlemskab | John Calmar" />
        <meta
          property="og:description"
          content="Køb medlemskab af John Calmars Univers, online klarsynsaftener, clairvoyance-sessioner, rygestop-forløb og hypnose/hypnoterapi-sessioner."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-spirituel-vejledning.png"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Online Butik – Book Sessioner & Medlemskab | John Calmar" />
        <meta
          name="twitter:description"
          content="Køb medlemskab af John Calmars Univers, online klarsynsaftener, clairvoyance-sessioner og hypnose."
        />
        <meta
          name="twitter:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-spirituel-vejledning.png"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-brand-50 py-16 md:py-24 overflow-hidden border-b border-brand-200/40">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-6 text-center space-y-6 relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-neutral-950 leading-tight">
            Læs mere om <span className="font-display italic text-brand-600">hvad jeg tilbyder</span>
          </h1>

          <p className="text-neutral-600 text-lg font-light leading-relaxed max-w-2xl mx-auto">
            Her kan du købe medlemskab af{" "}
            <Link
              to="/"
              className="text-neutral-900 font-medium hover:text-brand-600 underline underline-offset-4 decoration-brand-400/60 hover:decoration-brand-600 transition-colors"
            >
              John Calmar
            </Link>
            s Univers, online klarsynsaftener, clairvoyance-sessioner, rygestop-forløb og hypnose/hypnoterapi-sessioner.
          </p>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <span className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-neutral-500 text-sm">Henter produkter...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-brand-200 rounded-3xl">
              <p className="text-neutral-500">Ingen produkter fundet i butikken.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-white border border-brand-200/60 rounded-3xl overflow-hidden flex flex-col justify-between shadow-md"
                >
                  <div>
                    {/* Product Image */}
                    {product.image && product.image.length > 0 && (
                      <div className="aspect-[16/10] overflow-hidden border-b border-brand-200/60 bg-white">
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${product.image[0].replace("https://assets.auxodev.dk/", "")}`}
                          alt={product.title ? `${product.title} – John Calmar clairvoyance og hypnose Aarhus` : "Produkt i butikken hos John Calmar"}
                          className={
                            product.title?.includes("Bliv medlem af John Calmars Univers") ||
                            product.title?.includes("Online klarsyn for den kommende uge")
                              ? "w-full h-full object-contain object-top"
                              : "w-full h-full object-cover"
                          }
                        />
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="p-6 md:p-8 space-y-4 text-left">
                      <h3 className="font-display text-xl sm:text-2xl font-medium text-neutral-950 leading-snug break-words">
                        {(() => {
                          if (!product.title) return "";
                          // Match a dash followed by a number and "sessioner" or "session" (case-insensitive) at the end
                          const match = product.title.match(/(\s*[–\-\—]\s*\d+\s*sessioner[s]?)$/i);
                          if (match) {
                            const mainPart = product.title.substring(0, product.title.length - match[0].length);
                            const suffix = match[0].trim();
                            return (
                              <>
                                {mainPart}{" "}
                                <span className="inline-block whitespace-nowrap">{suffix}</span>
                              </>
                            );
                          }
                          return product.title;
                        })()}
                      </h3>
                      <div className="text-2xl font-bold text-brand-600 flex items-baseline gap-1.5 flex-wrap">
                        {(() => {
                          if (!product.price) return null;
                          const slashIndex = product.price.indexOf("/");
                          if (slashIndex !== -1) {
                            const main = product.price.slice(0, slashIndex).trim();
                            const unit = product.price.slice(slashIndex + 1).trim();
                            return (
                              <>
                                <span>{main}</span>
                                <span className="text-sm font-normal text-neutral-500">/ {unit}</span>
                              </>
                            );
                          }
                          const parenMatch = product.price.match(/^(.*?)\s*(\([^)]+\))$/);
                          if (parenMatch) {
                            return (
                              <>
                                <span>{parenMatch[1].trim()}</span>
                                <span className="text-sm font-normal text-neutral-500">{parenMatch[2].trim()}</span>
                              </>
                            );
                          }
                          return <span>{product.price}</span>;
                        })()}
                      </div>
                      <div
                        className="text-neutral-600 text-sm leading-relaxed font-light space-y-3"
                        dangerouslySetInnerHTML={{ __html: product.description }}
                      />
                    </div>
                  </div>

                  {/* Purchase Button / Instructions & Internal Link */}
                  <div className="p-6 md:p-8 pt-0 space-y-3">
                    <div className="w-full py-3.5 px-4 bg-brand-50 border border-brand-100 rounded-xl flex items-center justify-center gap-2.5 text-xs font-semibold uppercase tracking-wider text-brand-700">
                      <CreditCard className="w-4 h-4 text-brand-600" />
                      Køb via MobilePay / Bank
                    </div>

                    {/* Relevante interne links baseret på produkttype */}
                    {(() => {
                      const t = (product.title || "").toLowerCase();
                      if (t.includes("hypnose") || t.includes("hypnoterapi") || t.includes("rygestop")) {
                        return (
                          <Link
                            to="/hypnose"
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-neutral-600 hover:text-brand-700 transition-colors uppercase tracking-wider w-full pt-1"
                          >
                            <span>Læs om hypnose & forløb</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        );
                      }
                      if (t.includes("clairvoyance")) {
                        return (
                          <Link
                            to="/clairvoyance-online"
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-neutral-600 hover:text-brand-700 transition-colors uppercase tracking-wider w-full pt-1"
                          >
                            <span>Læs om clairvoyance sessioner</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        );
                      }
                      if (t.includes("univers") || t.includes("klarsyn")) {
                        return (
                          <Link
                            to="/calmars-univers"
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-neutral-600 hover:text-brand-700 transition-colors uppercase tracking-wider w-full pt-1"
                          >
                            <span>Læs om Calmars Univers & klarsyn</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />
    </>
  );
}
